// @djsem/ui — Stem-to-Visual/Lighting Trigger Mapping model (Canonical Prompt 138).
//
// Pure, framework-free logic for mapping approved-stem trigger events to visual scene
// behavior and to lighting/cue SIMULATION targets. Simulation/dry-run only by default — no
// real hardware output path exists here or anywhere this module touches (prompt 138 §3).
//
// Honesty scoping: prompt 138 §6 lists 12 trigger source kinds (vocal onset, vocal phrase
// region, breakdown region, drop region, ...). This repo's real stem contract
// (DJSEMStemRuntimeState in @djsem/analysis-contract's stemAlignment.ts) only computes
// per-stem energy + onset-active at a point in time — it has no phrase/breakdown/drop
// section detection. So this model supports the subset that's honestly backed by real data
// (onset + energy-threshold per stem type) rather than fabricating section-aware triggers
// that nothing in this repo computes.

import type { DJSEMStemTypeUI } from "./stemSetLanesModel";

/** The kind of runtime stem event a mapping can react to. */
export type StemTriggerKind = "onset" | "energy_threshold";

/** A selectable stem-trigger source option (stem type + kind). */
export interface StemTriggerSourceOption {
  stemType: DJSEMStemTypeUI;
  kind: StemTriggerKind;
  label: string;
}

const KIND_LABEL: Record<StemTriggerKind, string> = { onset: "onset", energy_threshold: "energy threshold" };
const STEM_LABEL: Record<DJSEMStemTypeUI, string> = { vocals: "Vocal", drums: "Drum", bass: "Bass", melody: "Melody", other: "Other/FX" };

/**
 * Trigger sources honestly available given which stem types are approved for this track.
 * @param availableStemTypes - Stem types approved for this track.
 * @returns One option per (stem type × trigger kind) combination.
 */
export function stemTriggerSourceOptions(availableStemTypes: DJSEMStemTypeUI[]): StemTriggerSourceOption[] {
  const kinds: StemTriggerKind[] = ["onset", "energy_threshold"];
  return availableStemTypes.flatMap((stemType) =>
    kinds.map((kind) => ({ stemType, kind, label: `${STEM_LABEL[stemType]} ${KIND_LABEL[kind]}` })),
  );
}

// ---- mapping targets (prompt 138 §7/§8) -------------------------------------

/** Visual-scene mapping targets a stem trigger may drive. */
export const VISUAL_MAPPING_TARGETS = [
  "scene_change", "scene_intensity", "motion_intensity", "particle_density", "colour_palette_shift",
  "symbol_appearance", "lyric_typography_emphasis", "camera_zoom", "background_pulse", "visual_blackout_safe_fallback",
] as const;
/** One of the {@link VISUAL_MAPPING_TARGETS}. */
export type VisualMappingTarget = (typeof VISUAL_MAPPING_TARGETS)[number];

/** Lighting/cue SIMULATION targets a stem trigger may drive (never real hardware output). */
export const LIGHTING_CUE_SIM_TARGETS = [
  "cue_marker", "section_cue", "impact_cue", "pulse_cue", "strobe_simulation_label",
  "colour_shift_simulation", "blackout_marker", "safe_fallback_marker", "operator_note",
] as const;
/** One of the {@link LIGHTING_CUE_SIM_TARGETS}. */
export type LightingCueSimTarget = (typeof LIGHTING_CUE_SIM_TARGETS)[number];

/**
 * Human-readable label for a mapping target.
 * @param target - A visual or lighting-cue-simulation target.
 * @returns The display label (title-cased, underscores replaced with spaces).
 */
export function mappingTargetLabel(target: VisualMappingTarget | LightingCueSimTarget): string {
  return target.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---- mapping ----------------------------------------------------------------

/** A configured mapping from a stem trigger to a visual or lighting-cue-simulation target. */
export interface StemTriggerMapping {
  id: string;
  stemType: DJSEMStemTypeUI;
  triggerKind: StemTriggerKind;
  /** 0..1, used only for energy_threshold; ignored for onset. */
  threshold: number;
  targetKind: "visual" | "lighting_cue";
  target: VisualMappingTarget | LightingCueSimTarget;
  disabled: boolean;
  operatorNote?: string;
}

/** Input to create a new {@link StemTriggerMapping}. */
export interface CreateStemTriggerMappingInput {
  id: string;
  stemType: DJSEMStemTypeUI;
  triggerKind: StemTriggerKind;
  threshold?: number;
  targetKind: "visual" | "lighting_cue";
  target: VisualMappingTarget | LightingCueSimTarget;
  operatorNote?: string;
}

/**
 * Create a new stem-trigger mapping (enabled by default, threshold clamped to 0..1).
 * @param input - The mapping definition.
 * @returns The created {@link StemTriggerMapping}.
 */
export function createStemTriggerMapping(input: CreateStemTriggerMappingInput): StemTriggerMapping {
  return {
    id: input.id, stemType: input.stemType, triggerKind: input.triggerKind,
    threshold: Math.max(0, Math.min(1, input.threshold ?? 0.5)),
    targetKind: input.targetKind, target: input.target, disabled: false, operatorNote: input.operatorNote,
  };
}

// ---- preview / evaluation ----------------------------------------------------

/** A point-in-time runtime reading for one stem (energy + onset state), used to evaluate mappings. */
export interface StemRuntimeSample { stemType: DJSEMStemTypeUI; energy: number; onsetActive: boolean; available: boolean; }

/** Result of evaluating a mapping against a runtime sample. */
export interface StemTriggerEvaluation {
  fired: boolean;
  value: number;
  warnings: string[];
}

/**
 * Evaluate a mapping against a runtime sample (e.g. DJSEMStemRuntimeState.perStem at a timeMs).
 * @param mapping - The mapping to evaluate.
 * @param samples - Runtime samples for the current instant, one per stem type.
 * @returns Whether it fired, the underlying value, and any warnings (e.g. stem unavailable).
 */
export function evaluateStemTriggerMapping(mapping: StemTriggerMapping, samples: StemRuntimeSample[]): StemTriggerEvaluation {
  if (mapping.disabled) return { fired: false, value: 0, warnings: ["Mapping is disabled."] };
  const sample = samples.find((s) => s.stemType === mapping.stemType);
  if (!sample || !sample.available) return { fired: false, value: 0, warnings: [`${STEM_LABEL[mapping.stemType]} stem unavailable at this time.`] };
  if (mapping.triggerKind === "onset") return { fired: sample.onsetActive, value: sample.energy, warnings: [] };
  return { fired: sample.energy >= mapping.threshold, value: sample.energy, warnings: [] };
}

// ---- safety (prompt 138 §3/§8) -----------------------------------------------

/**
 * Lighting/cue targets always carry the simulation/dry-run disclaimer — never real hardware output.
 * @param targetKind - The mapping's target kind.
 * @returns True if `targetKind` is `"lighting_cue"`.
 */
export function isLightingCueTarget(targetKind: "visual" | "lighting_cue"): boolean {
  return targetKind === "lighting_cue";
}
